import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './store/auth';

// Layouts
import { PublicLayout } from './components/Layout';
import { DashboardLayout } from './components/DashboardLayout';
import { AdminLayout } from './components/AdminLayout';

// Public Pages
import { HomePage } from './pages/public/Home';
import { TrackRentalsPage } from './pages/public/TrackRentals';
import { CalendarPage } from './pages/public/Calendar';
import { BookPage } from './pages/public/Book';
import { AboutPage } from './pages/public/About';
import { TicketsPage } from './pages/public/Tickets';
import { ShopPage } from './pages/public/Shop';
import { CartPage } from './pages/public/Cart';
import { CheckoutPage } from './pages/public/Checkout';
import { FoodPage } from './pages/public/Food';
import { MapPage } from './pages/public/Map';
import { NavigatePage } from './pages/public/Navigate';
import { EventDetailPage } from './pages/public/EventDetail';

// Auth Pages
import { LoginPage } from './pages/auth/Login';
import { RegisterPage } from './pages/auth/Register';

// Dashboard Pages
import { DashboardHome } from './pages/dashboard/Home';
import { GaragePage } from './pages/dashboard/Garage';

// Team Pages
import { TeamDashboard } from './pages/team/Dashboard';
import { TeamMembers } from './pages/team/Members';
import { TeamBookings } from './pages/team/Bookings';
import { NewBooking } from './pages/team/NewBooking';

// Admin Pages
import { AdminDashboard } from './pages/admin/Dashboard';
import { AdminBookings } from './pages/admin/Bookings';
import { AdminTeams } from './pages/admin/Teams';
import { AdminTracks } from './pages/admin/Tracks';
import { AdminPOIs } from './pages/admin/POIs';
import { AdminEventsPage } from './pages/admin/Events';
import { AdminSettings } from './pages/admin/Settings';

// Components
import { Toast } from './components/ui/Toast';

// Protected Route wrapper
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

// Admin Route wrapper
function AdminRoute() {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user?.isSiteAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

export default function App() {
  const { isAuthenticated, fetchUser, token } = useAuthStore();

  // Fetch user on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUser();
    }
  }, [isAuthenticated, token, fetchUser]);

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/track-rentals" element={<TrackRentalsPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/:slug" element={<EventDetailPage />} />
          <Route path="/shop" element={<ShopPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/food" element={<FoodPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/navigate" element={<NavigatePage />} />
        </Route>

        {/* Auth Routes (no layout) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Protected Dashboard Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/garage" element={<GaragePage />} />

            {/* Team Routes */}
            <Route path="/team/:slug" element={<TeamDashboard />} />
            <Route path="/team/:slug/members" element={<TeamMembers />} />
            <Route path="/team/:slug/bookings" element={<TeamBookings />} />
            <Route path="/team/:slug/bookings/new" element={<NewBooking />} />
          </Route>
        </Route>

        {/* Admin Routes */}
        <Route element={<AdminRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/bookings" element={<AdminBookings />} />
            <Route path="/admin/teams" element={<AdminTeams />} />
            <Route path="/admin/tracks" element={<AdminTracks />} />
            <Route path="/admin/pois" element={<AdminPOIs />} />
            <Route path="/admin/events" element={<AdminEventsPage />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Toast */}
      <Toast />
    </>
  );
}
